/**
 * 域名配置管理
 * 统一管理所有环境的域名配置
 */

export type Environment = 'production' | 'test' | 'development';

/**
 * 环境检测模式匹配
 * 通过域名模式判断当前环境
 */
const ENV_PATTERNS: Record<Environment, string[]> = {
  production: ['pengi.ai', 'tutor.pengi.ai', 'flowquiz.ai'],
  test: ['learn-test.nanci.ai', 'learn-test.acornx.ai'],
  development: ['dev.nanci.ai', 'localhost', '127.0.0.1'],
};

/**
 * CDN域名（所有环境统一）
 */
const CDN_DOMAIN = 'syllabus-cdn.pengi.ai';

/**
 * 获取当前环境
 * @param hostname - 可选的主机名，用于SSR或测试场景
 * @returns 当前环境类型
 */
export function getEnv(hostname?: string): Environment {
  // SSR环境处理
  if (typeof window === 'undefined' && !hostname) {
    return process.env.NODE_ENV === 'production' ? 'production' : 'development';
  }

  const host = (hostname || window.location.hostname).toLowerCase().split(':')[0];

  // 更精确的域名匹配：
  // - 仅支持 exact 匹配（example.com）
  const hostMatches = (pattern: string) => host === pattern;
  const checkOrder: Environment[] = ['test', 'production', 'development'];

  // 通过模式匹配判断环境
  for (const env of checkOrder) {
    const patterns = ENV_PATTERNS[env];
    if (patterns.some(hostMatches)) {
      return env;
    }
  }

  // 默认为开发环境
  return 'development';
}

/**
 * 判断是否为测试环境
 * @param hostname - 可选的主机名
 */
export function isTest(hostname?: string): boolean {
  return getEnv(hostname) === 'test';
}

/**
 * 判断是否为生产环境
 * @param hostname - 可选的主机名
 */
export function isProd(hostname?: string): boolean {
  return getEnv(hostname) === 'production';
}

/**
 * 判断是否为开发环境
 * @param hostname - 可选的主机名
 */
export function isDev(hostname?: string): boolean {
  return getEnv(hostname) === 'development';
}

/**
 * 构建CDN资源URL
 * @param path - 资源路径
 * @returns 完整的CDN URL
 */
export function cdnUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `https://${CDN_DOMAIN}/${cleanPath}`;
}
